import { FormUnit } from "@suse-o11y/signal/form";
import React from "react";

import { Button } from "~/components/button";
import { Dropdown } from "~/components/dropdown";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { IconMagnifyingGlass } from "~/components/icon";
import { Input } from "~/components/input";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { usePermanent } from "~/tools/use-permanent";

import { TableHeadCellButton } from "./_head-cell-button";
import { TableColFilter } from "./col-filter";

const DEFAULT_COL_FILTER = TableColFilter.init();

export interface TableHeadCellFilterProps {
	filter: undefined | TableColFilter;
	onFilterChange: (filter: TableColFilter) => void;
}

export const TableHeadCellFilter: React.FC<TableHeadCellFilterProps> = ({
	filter = DEFAULT_COL_FILTER,
	onFilterChange,
}) => {
	const [visible, setVisible] = React.useState(false);

	return (
		<Tooltip>
			<Dropdown
				align={{ offset: [0, 4] }}
				destroyPopupOnHide
				onVisibleChange={setVisible}
				overlay={
					<FilterOverlay
						initialValue={filter.query}
						onSubmit={(next) => {
							onFilterChange(filter.change(next.trim()));
							setVisible(false);
						}}
					/>
				}
				placement="bottomRight"
				trigger={["click"]}
				visible={visible}
			>
				<TooltipTrigger>
					<TableHeadCellButton className="min-w-6" isHighlighted={visible} isSelected={filter.query !== ""}>
						<IconMagnifyingGlass className="text-13" />
					</TableHeadCellButton>
				</TooltipTrigger>
			</Dropdown>

			<TooltipContent>{filter.query === "" ? "Filter" : `Filtered by "${filter.query}"`}</TooltipContent>
		</Tooltip>
	);
};

const FilterOverlay: React.FC<{
	initialValue: string;
	onSubmit: React.Dispatch<string>;
}> = ({ initialValue, onSubmit }) => {
	const state = usePermanent(() =>
		FormUnit(initialValue, {
			initial: "",
		}),
	);

	return (
		<Form
			className="grid min-w-50 grid-cols-2 gap-2 rounded-sm bg-white p-2 shadow-md"
			onReset={() => {
				onSubmit("");
			}}
			onSubmit={(query) => {
				onSubmit(query);
			}}
			state={state}
		>
			<FormField className="col-span-full" state={state}>
				{(monitor, field) => (
					<Input
						autoFocus
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="Search"
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>

			<Button className="uppercase" size="sm" type="reset" variant="tertiary">
				Reset
			</Button>

			<Button className="uppercase" size="sm" type="submit" variant="primary">
				Search
			</Button>
		</Form>
	);
};
